import { useState } from "react";
import {
  Layers,
  AlertCircle,
  Plus,
  ToggleLeft,
  ToggleRight,
  Pencil,
  Shield,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminModal, EmptyState } from "@/components/admin/shared";
import {
  useStackingRules,
  useCreateStackingRule,
  useToggleStackingRule,
} from "@/hooks/admin/useMarketing";
import type {
  PromoType,
  CreateStackingRuleInput,
} from "@/lib/admin-api/marketing";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PROMO_TYPE_LABELS: Record<PromoType, string> = {
  percentage: "Pourcentage",
  fixed_amount: "Montant fixe",
  free_shipping: "Livraison gratuite",
  buy_x_get_y: "Achetez X, obtenez Y",
};

const PROMO_TYPE_COLORS: Record<PromoType, string> = {
  percentage: "bg-purple-100 text-purple-800 border-purple-200",
  fixed_amount: "bg-blue-100 text-blue-800 border-blue-200",
  free_shipping: "bg-teal-100 text-teal-800 border-teal-200",
  buy_x_get_y: "bg-orange-100 text-orange-800 border-orange-200",
};

// ---------------------------------------------------------------------------
// Create rule modal
// ---------------------------------------------------------------------------

function CreateRuleModal({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const create = useCreateStackingRule();
  const [name, setName] = useState("");
  const [allowedTypes, setAllowedTypes] = useState<Set<PromoType>>(new Set());
  const [maxStack, setMaxStack] = useState("2");
  const [maxDiscountPercent, setMaxDiscountPercent] = useState("");
  const [priorityOrder, setPriorityOrder] = useState("");

  const toggleType = (t: PromoType) => {
    const next = new Set(allowedTypes);
    if (next.has(t)) {
      next.delete(t);
    } else {
      next.add(t);
    }
    setAllowedTypes(next);
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error("Le nom est obligatoire.");
      return;
    }
    if (allowedTypes.size === 0) {
      toast.error("Selectionnez au moins un type autorise.");
      return;
    }
    const ms = parseInt(maxStack, 10);
    if (isNaN(ms) || ms < 1) {
      toast.error("Max combinaisons invalide.");
      return;
    }
    if (ms > 10) {
      toast.error("Maximum 10 combinaisons.");
      return;
    }

    if (maxDiscountPercent) {
      const mdp = parseFloat(maxDiscountPercent);
      if (isNaN(mdp) || mdp <= 0 || mdp > 100) {
        toast.error(
          "Le plafond de remise doit être un pourcentage entre 1 et 100.",
        );
        return;
      }
    }

    const input: CreateStackingRuleInput = {
      name,
      allowed_types: Array.from(allowedTypes),
      max_stack: ms,
      priority_order: priorityOrder,
    };
    create.mutate(input, {
      onSuccess: () => {
        toast.success("Regle creee.");
        onClose();
      },
      onError: () => toast.error("Erreur lors de la creation de la regle."),
    });
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Nouvelle regle de cumul"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? "En cours..." : "Creer"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Name */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Nom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex : Regle standard \u2014 2 promos max"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>

        {/* Allowed types */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-stone-700">
            Types autorisés <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-stone-400">
            Sélectionnez les types de promotions qui peuvent être combinés.
          </p>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(PROMO_TYPE_LABELS) as PromoType[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => toggleType(t)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  allowedTypes.has(t)
                    ? "border-amber-400 bg-amber-100 text-amber-800"
                    : "border-stone-200 bg-white text-stone-600 hover:border-stone-300"
                }`}
              >
                {PROMO_TYPE_LABELS[t]}
              </button>
            ))}
          </div>
        </div>

        {/* Max stack + Max discount */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Max combinaisons <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={maxStack}
              onChange={(e) => setMaxStack(e.target.value)}
              min={1}
              max={10}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
            <p className="text-xs text-stone-400">
              Nombre max de promos appliquees simultanement.
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Plafond remise max (%)
            </label>
            <div className="relative">
              <input
                type="number"
                value={maxDiscountPercent}
                onChange={(e) => setMaxDiscountPercent(e.target.value)}
                placeholder="ex : 50"
                min={1}
                max={100}
                className="w-full rounded-lg border border-stone-200 px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-stone-400">
                %
              </span>
            </div>
            <p className="text-xs text-stone-400">
              Remise cumulee max (vide = pas de plafond).
            </p>
          </div>
        </div>

        {/* Priority order */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Ordre de priorite
          </label>
          <input
            type="text"
            value={priorityOrder}
            onChange={(e) => setPriorityOrder(e.target.value)}
            placeholder="ex : percentage, fixed_amount, free_shipping"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
          <p className="text-xs text-stone-400">
            Types separes par des virgules, du plus prioritaire au moins
            prioritaire.
          </p>
        </div>

        {/* Info */}
        <div className="rounded-lg bg-stone-50 border border-stone-200 px-4 py-3 flex items-start gap-2">
          <Shield className="h-4 w-4 shrink-0 text-stone-500 mt-0.5" />
          <div className="text-xs text-stone-600 space-y-1">
            <p className="font-medium">Comment fonctionne le cumul ?</p>
            <p>
              Quand un client applique plusieurs promotions, le systeme
              respecte ces regles : nombre max de combinaisons, plafond de
              remise, et ordre de priorite.
            </p>
          </div>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Rule card (visual alternative to table for rules)
// ---------------------------------------------------------------------------

function RuleCard({
  rule,
  onToggle,
}: {
  readonly rule: {
    readonly id: string;
    readonly name: string;
    readonly allowed_types: readonly PromoType[];
    readonly max_stack: number;
    readonly priority_order: string;
    readonly is_active: boolean;
  };
  readonly onToggle: (id: string) => void;
}) {
  const priorityTypes = rule.priority_order
    ? rule.priority_order
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  return (
    <div
      className={`rounded-xl border bg-white p-5 space-y-4 transition-shadow hover:shadow-sm ${
        rule.is_active ? "border-stone-200" : "border-stone-100 opacity-60"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 className="font-medium text-stone-900">{rule.name}</h3>
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <span>Max {rule.max_stack} combinaison(s)</span>
          </div>
        </div>
        <button
          onClick={() => onToggle(rule.id)}
          className={`shrink-0 transition-colors ${
            rule.is_active
              ? "text-emerald-600 hover:text-emerald-800"
              : "text-stone-400 hover:text-stone-600"
          }`}
          title={rule.is_active ? "Desactiver" : "Activer"}
        >
          {rule.is_active ? (
            <ToggleRight className="h-5 w-5" />
          ) : (
            <ToggleLeft className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Allowed types */}
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">
          Types autorises
        </p>
        <div className="flex flex-wrap gap-1.5">
          {rule.allowed_types.map((t) => (
            <span
              key={t}
              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${PROMO_TYPE_COLORS[t]}`}
            >
              {PROMO_TYPE_LABELS[t]}
            </span>
          ))}
        </div>
      </div>

      {/* Priority order */}
      {priorityTypes.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">
            Ordre de priorite
          </p>
          <div className="flex items-center gap-1">
            {priorityTypes.map((t, idx) => (
              <div key={t} className="flex items-center gap-1">
                {idx > 0 && (
                  <span className="text-stone-300">\u2192</span>
                )}
                <span className="rounded bg-stone-100 px-2 py-0.5 text-xs font-mono text-stone-600">
                  {PROMO_TYPE_LABELS[t as PromoType] ?? t}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function StackingRules() {
  const [modalOpen, setModalOpen] = useState(false);
  const { data, isLoading, isError } = useStackingRules();
  const toggle = useToggleStackingRule();
  const rules = data?.rules ?? [];

  const activeCount = rules.filter((r) => r.is_active).length;

  const handleToggle = (id: string) => {
    toggle.mutate(id, {
      onSuccess: () => toast.success("Statut mis a jour."),
      onError: () => toast.error("Erreur lors du changement de statut."),
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Règles de cumul
              </h1>
              <p className="text-sm text-stone-500">
                Définissez quelles promotions peuvent être combinées
              </p>
            </div>
          </div>
          <Button onClick={() => setModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle regle
          </Button>
        </div>

        {/* Summary */}
        <div className="rounded-lg border border-stone-200 bg-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-5 w-5 text-stone-400" />
            <div>
              <p className="text-sm font-medium text-stone-700">
                {activeCount} regle(s) active(s) sur {rules.length}
              </p>
              <p className="text-xs text-stone-400">
                Les regles actives controlent le comportement de cumul des
                promotions.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 animate-pulse">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-40 rounded-xl bg-stone-100" />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Impossible de charger les regles.
          </div>
        )}

        {!isLoading && !isError && rules.length === 0 && (
          <EmptyState
            icon={<Layers className="h-6 w-6" />}
            title="Aucune regle de cumul"
            description="Definissez des regles pour controler comment les promotions se combinent."
            action={{
              label: "Nouvelle regle",
              onClick: () => setModalOpen(true),
            }}
          />
        )}

        {!isLoading && !isError && rules.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {rules.map((r) => (
              <RuleCard key={r.id} rule={r} onToggle={handleToggle} />
            ))}
          </div>
        )}
      </div>

      <CreateRuleModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </AdminLayout>
  );
}
